import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'components/mui/Modal'
import Box from 'components/mui/Box'
import Typography from 'components/mui/Typography'
import Divider from 'components/mui/Divider'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import Button from 'components/mui/Button'
import { IconButton } from '@mui/material'
import { Close, Check } from '@mui/icons-material'
import { typography } from 'theme'

/**
 * PAN KYC Info Modal - matches the shared design for KYC details confirmation.
 */
const PanKycInfoModal = ({
  open,
  onClose,
  onConfirm,
  onUpdateContact,
  onReKyc,
  data
}) => {
  const {
    fullName,
    panNumber,
    dateOfBirth,
    mobileNumber,
    emailAddress,
    address
  } = data || {}

  const containerSx = {
    bgcolor: '#0B1120',
    borderRadius: '16px',
    p: { xs: 2.5, sm: 3 },
    position: 'relative',
    width: { xs: '100%', sm: 520, md: 560 },
    color: '#FFFFFF',
    boxShadow: '0px 16px 48px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.08)'
  }

  const sectionLabel = {
    color: '#94A3B8',
    fontSize: '13px',
    fontWeight: typography.fontWeight.regular,
    mb: 0.75,
    lineHeight: 1.2
  }

  const sectionValue = {
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.45
  }

  const twoCol = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: { xs: 2, sm: 2.5 },
    width: '100%'
  }

  const rowDivider = {
    my: 2,
    borderColor: 'rgba(255,255,255,0.08)'
  }

  const secondaryButtonSx = {
    color: '#E2E8F0',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: typography.fontWeight.medium,
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    height: 48,
    width: '100%',
    justifyContent: 'center',
    '&:hover': { backgroundColor: '#1A1F33' }
  }

  const renderField = (label, value) => (
    <Box>
      <Typography sx={sectionLabel}>{label}</Typography>
      <Typography sx={sectionValue}>{value || '--'}</Typography>
    </Box>
  )

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={containerSx}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#E2E8F0',
            backgroundColor: 'rgba(255,255,255,0.08)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' }
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 2.5, px: { xs: 0, sm: 3 } }}>
          <Typography
            sx={{
              fontSize: '19px',
              fontWeight: typography.fontWeight.bold,
              color: '#FFFFFF',
              mb: 0.75
            }}
          >
            Your KYC Information
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#A3B1CC',
              lineHeight: 1.5
            }}
          >
            Confirm your details so we can keep your journey safe and seamless.
          </Typography>
        </Box>

        {/* Verified banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.5,
            mb: 2,
            borderRadius: '10px',
            backgroundColor: 'rgba(40,199,111,0.10)',
            border: '1px solid rgba(40,199,111,0.30)'
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: '#28C76F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Check sx={{ fontSize: 14, color: '#FFFFFF' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: typography.fontWeight.medium,
              color: '#28C76F'
            }}
          >
            Fetched &amp; verified from your KYC record
          </Typography>
        </Box>

        {/* Details card */}
        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: 'rgba(255,255,255,0.02)',
            p: { xs: 2, sm: 2.5 }
          }}
        >
          <Box sx={twoCol}>
            {renderField('Full Name', fullName)}
            {renderField('PAN Number', panNumber)}
          </Box>

          <Divider sx={rowDivider} />

          <Box sx={twoCol}>
            {renderField('Date of Birth', dateOfBirth)}
            {renderField('Mobile Number', mobileNumber)}
          </Box>

          <Divider sx={rowDivider} />

          {renderField('Email Address', emailAddress)}

          <Divider sx={rowDivider} />

          {renderField('Address', address)}
        </Box>

        <Box sx={{ mt: 3 }}>
          <AppButtonComponent
            onClick={onConfirm}
            sx={{
              width: '100%',
              backgroundColor: '#0EA5E9',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              height: 52,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0B8CC9', boxShadow: 'none' }
            }}
          >
            Confirm and Proceed
          </AppButtonComponent>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mt: 1.5
          }}
        >
          <Button variant="text" onClick={onUpdateContact} sx={secondaryButtonSx}>
            Update my contact details
          </Button>

          <Button variant="text" onClick={onReKyc} sx={secondaryButtonSx}>
            Start Re-KYC
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

PanKycInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUpdateContact: PropTypes.func.isRequired,
  onReKyc: PropTypes.func.isRequired,
  data: PropTypes.shape({
    fullName: PropTypes.string,
    panNumber: PropTypes.string,
    dateOfBirth: PropTypes.string,
    mobileNumber: PropTypes.string,
    emailAddress: PropTypes.string,
    address: PropTypes.string
  })
}

PanKycInfoModal.defaultProps = {
  data: {}
}

export default PanKycInfoModal
